import * as React from "react";
import CclGenericAccordian from "../../../../components/common/cclGenericAccordian";
import { AlertColor, Box, Stack } from "@mui/material";
import WorkEntryDataGrid from "./workEntryDataGrid";
import { CclInfoTblSessionPayrollOneTemplate } from "./sessionPayrollTemplateOne";
import { CclInfoTblSessionPayrollTwoTemplate } from "./sessionPayrollTemplateTwo";
import CclInfoTable, {
    CclInfoTableFormValue,
    CclInfoTableRowTemplate,
} from "../../../../components/common/cclInfoTable/cclInfoTable";
import { cclTokenedPayrollApi } from "../../../../services/cclTokenedPayrollApi";
import TrainerEntryDataGrid from "./trainerEntryDetail/trainerEntryDataGrid";
import { CreatePayrollEventRequest } from "../../../../services/types/payrollEventTypes";
import { eInfoTableValueFieldEditType } from "../../../../components/common/cclInfoTable/cclInfoTableValueField";
import { SessionDetailType } from "../../../../services/types/sessionDetailTypes";
import PayrollNotes from "./payrollNotes";
import CclAlertSnackbar from "../../../../components/common/cclAlertSnackbar";
import useLogAccessEvent from "../../../../hooks/useLogAccessEvent";
import { AccessEventOFCReducedDetails } from "../../../../services/types/accessEventTypes";

export interface SessionPayrollPanelProps {
    sessionId: string;
    sendToReport: () => void;
    projectId: string;
    projectCode: string;
    projectName: string;
    payroll: any;
    session: SessionDetailType;
    refetchPayroll: () => void;
}

const SessionPayrollPanel: React.FC<SessionPayrollPanelProps> = (props) => {

    const [template, setTemplate] = React.useState<CclInfoTableRowTemplate[]>(
        CclInfoTblSessionPayrollOneTemplate.map((row) => ({
            ...row,
            valueField: {
                ...row.valueField,
            },
        }))
    );
    const [template2, setTemplate2] = React.useState<CclInfoTableRowTemplate[]>(
        CclInfoTblSessionPayrollTwoTemplate.map((row) => ({
            ...row,
            valueField: {
                ...row.valueField,
            },
        }))
    );
    const [workEntryId, setWorkEntryId] = React.useState<number>(0);
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>("info");
    const { logEvent } = useLogAccessEvent();

    const { data: referenceData } = cclTokenedPayrollApi.useGetReferenceDataQuery();
    const { data: glData } = cclTokenedPayrollApi.useGetGlDataQuery();
    const {data: clientData} = cclTokenedPayrollApi.useGetClientListQuery();

    const [getWorkEntryDetails, { data: workEntryData, isLoading: workEntryDetailsLoading, isError }] = cclTokenedPayrollApi.useLazyGetWorkEntryByIdQuery();
    const [createPayrollEvent, {isLoading: isCreatingPayrollProject}] = cclTokenedPayrollApi.useCreatePayrollEventMutation();
    const [updatePayrollProject, { isLoading: isUpdatingPayrollProject }] = cclTokenedPayrollApi.useUpdatePayrollProjectMutation();

    const revRecDate = props.session.typeKey === 6 ? props.session.start : props.session.end;

    const getLobDisplayValue = React.useCallback(
        (rawValue: string | null | undefined): string => {
            if (!rawValue) {
                return "";
            }

            rawValue = String(rawValue);

            const lobOptions: any[] = referenceData?.linesOfBusiness ?? [];
            const normalizedRaw = rawValue.trim().toLowerCase();
            const matchedLob = lobOptions.find((lob: any) => {
                const code = String(lob?.lobCode ?? "").trim().toLowerCase();
                const description = String(lob?.lobDescription ?? lob?.description ?? "")
                    .trim()
                    .toLowerCase();
                return code === normalizedRaw || description === normalizedRaw;
            });

            if (!matchedLob) {
                return rawValue;
            }

            const code = String(matchedLob?.lobCode ?? "").trim();
            const description = String(matchedLob?.lobDescription ?? matchedLob?.description ?? "").trim();

            if (code && description) {
                return `${code} - ${description}`;
            }

            return code || description || rawValue;
        },
        [referenceData]
    );

    const getAdminLocCodeValue = React.useCallback(
        (rawValue: string | null | undefined): string => {
            if (!rawValue) {
                return "";
            }

            const normalizedRaw = String(rawValue).trim().toLowerCase();
            if (!normalizedRaw) {
                return "";
            }

            const adminLocOptions: any[] = referenceData?.locations ?? [];
            const matchedLocation = adminLocOptions.find((location: any) => {
                const code = String(location?.locCode ?? "").trim().toLowerCase();
                const desc = String(location?.locDesc ?? "").trim().toLowerCase();
                const combined = `${code} - ${desc}`.trim();

                return (
                    code === normalizedRaw ||
                    desc === normalizedRaw ||
                    combined === normalizedRaw
                );
            });

            return String(matchedLocation?.locCode ?? rawValue);
        },
        [referenceData]
    );

    const payrollDisplayData = React.useMemo(() => {
        if (!props.payroll) {
            return {};
        }

        const { lob: _lob, adminLoc: _adminLoc, client: _client, ...basePayrollData } = props.payroll;

        const lobOptions: any[] = referenceData?.linesOfBusiness ?? [];
        const clientOptions: any[] = clientData ?? [];

        const lobName = lobOptions.find((lob: any) => lob.lobCode === props.payroll.lob);
        const clientName = clientOptions.find(
            (c: any) =>
                c.clientCode === props.payroll.client ||
                c.client === props.payroll.client ||
                c.clientId === props.payroll.client
        );

        const normalizedLob =
            getLobDisplayValue(props.payroll.lob) ||
            getLobDisplayValue(props.session.type) ||
            lobName?.lobDescription ||
            props.payroll.lob ||
            props.session.type ||
            "";
        const normalizedAdminLoc =
            getAdminLocCodeValue(props.payroll.adminLoc) ||
            getAdminLocCodeValue(props.session.managingLocation) ||
            props.payroll.adminLoc ||
            props.session.managingLocation ||
            "";
        const normalizedClient =
            clientName?.clientName ?? clientName?.client ?? props.payroll.client ?? props.session.customer ?? "";

        const tableData = {
            ...basePayrollData,
            lob: normalizedLob,
            adminLoc: normalizedAdminLoc,
            client: normalizedClient,
        };

        return tableData;
    }, [props.payroll, props.session, referenceData, clientData, getLobDisplayValue, getAdminLocCodeValue]);

    React.useEffect(() => {
        // Clone the template so each update has a new object reference.
        const t = CclInfoTblSessionPayrollOneTemplate.map((row) => ({
            ...row,
            valueField: {
                ...row.valueField,
            },
        }));

        const t2 = CclInfoTblSessionPayrollTwoTemplate.map((row) => ({
            ...row,
            valueField: {
                ...row.valueField,
            },
        }));

        // Only offer enabled GL Groups, but keep the one already assigned to this
        // payroll project even if it has since been disabled, so its value still
        // renders in the Select.
        const currentGlGroupCode = props.payroll?.glGroup ?? null;
        const glGroupOptions: any[] = (glData?.glGroups ?? []).filter(
            (group: any) => group?.active || group?.groupCode === currentGlGroupCode
        );
        const adminLocOptions: any[] = referenceData?.locations ?? [];

        let sortedGlGroupOptions = [...glGroupOptions].sort((a: any, b: any) => {
            const aCode = a.groupCode ?? "";
            const bCode = b.groupCode ?? "";
            return aCode.localeCompare(bCode);
        });

        let sortedAdminLocOptions = adminLocOptions.filter((location: any) => location?.active !== false).sort((a: any, b: any) => {
            const aCode = a.locCode ?? "";
            const bCode = b.locCode ?? "";
            return aCode.localeCompare(bCode);
        });

        const seenAdminLocCodes = new Set<string>();
        const seenAdminLocLabels = new Set<string>();
        const uniqueAdminLocOptions = sortedAdminLocOptions.filter((location: any) => {
            const normalizedCode = String(location?.locCode ?? "")
                .trim()
                .toLowerCase();
            const normalizedLabel = String(location?.locDesc ?? "")
                .trim()
                .toLowerCase();

            if (!normalizedCode || !normalizedLabel) {
                return false;
            }

            if (seenAdminLocCodes.has(normalizedCode) || seenAdminLocLabels.has(normalizedLabel)) {
                return false;
            }

            seenAdminLocCodes.add(normalizedCode);
            seenAdminLocLabels.add(normalizedLabel);
            return true;
        });

        if (!props.payroll) {
            let r = t.find((r) => r.id === "eventCode");
            if (r != null) {
                r.valueField.value = props.session.sessionCode;
                r.value = props.session.sessionCode;
            }

            r = t.find((r) => r.id === "lob");
            if (r != null) {
                const lobDisplay = getLobDisplayValue(props.session.type) || props.session.type;
                r.valueField.value = lobDisplay;
                r.value = lobDisplay;
            }

            r = t2.find((r) => r.id === "adminLoc");
            if (r != null) {
                r.valueField.editFieldType = eInfoTableValueFieldEditType.Select;
                r.valueField.selectOptions = uniqueAdminLocOptions.map((location: any) => ({
                    val: location.locCode,
                    option: `${location.locCode} - ${location.locDesc}`,
                }));
            }

            r = t2.find((r) => r.id === "projectName");
            if (r != null) {
                r.valueField.value = props.projectName;
                r.value = props.projectName;
            }

            r = t.find((r) => r.id === "client");
            if (r != null) {
                r.valueField.value = props.session.customer;
                r.value = props.session.customer;
            }

            r = t2.find((r) => r.id === "glGroup");
            if (r != null) {
                
                r.valueField.editFieldType = eInfoTableValueFieldEditType.Select;
                r.valueField.selectOptions = sortedGlGroupOptions.map((group: any) => ({
                    val: group.groupCode,
                    option: `${group.groupCode} - ${group.groupDesc}`,
                }));
            }

            setTemplate(t);
            setTemplate2(t2);
            return;
        }

        let r = t.find((r) => r.id === "eventCode");
        if (r != null) {
            r.valueField.value = props.session.sessionCode;
            r.value = props.session.sessionCode;
        }
        r = t2.find((r) => r.id === "projectName");
        if (r != null) {
            r.valueField.value = props.projectName;
            r.value = props.projectName;
        }
        r = t.find((r) => r.id === "lob");
        if (r != null) {
            const lobDisplay = getLobDisplayValue(props.session.type) || props.session.type;
            r.valueField.value = lobDisplay;
            r.value = lobDisplay;
        }
        r = t2.find((r) => r.id === "adminLoc");
        if (r != null) {
            const resolvedAdminLoc =
                getAdminLocCodeValue(props.payroll.adminLoc) ||
                getAdminLocCodeValue(props.session.managingLocation) ||
                props.payroll.adminLoc ||
                props.session.managingLocation ||
                "";

            r.valueField.value = resolvedAdminLoc;
            r.value = resolvedAdminLoc;
            r.valueField.editFieldType = eInfoTableValueFieldEditType.Select;
            r.valueField.selectOptions = uniqueAdminLocOptions.map((location: any) => ({
                val: location.locCode,
                option: `${location.locCode} - ${location.locDesc}`,
            }));
        }
        r = t.find((r) => r.id === "client");
        if (r != null) {
            r.valueField.value = props.session.customer;
            r.value = props.session.customer;
        }
        r = t2.find((r) => r.id === "glGroup");
        if (r != null) {
            r.valueField.editFieldType = eInfoTableValueFieldEditType.Select;
            r.valueField.selectOptions = sortedGlGroupOptions.map((group: any) => ({
                val: group.groupCode,
                option: `${group.groupCode} - ${group.groupDesc}`,
            }));
        }
        setTemplate(t);
        setTemplate2(t2);
    }, [
        props.payroll,
        props.session,
        props.sessionId,
        props.projectName,
        referenceData,
        glData,
        clientData,
        getLobDisplayValue,
        getAdminLocCodeValue,
    ]);

    const displayWorkEntryDetails = (workEntryId: number) => {
        setWorkEntryId(workEntryId);
        getWorkEntryDetails(String(workEntryId));
    }

    const getGlGroupFromChanges = (changes: CclInfoTableFormValue[]): string | null => {
        const glGroupChange = changes.find((row) => row.name === "glGroup");
        if (!glGroupChange) {
            return props.payroll?.glGroup ?? null;
        }

        const { value } = glGroupChange;
        if (value == null || value === "") {
            return null;
        }

        if (typeof value === "object" && "val" in value) {
            return value.val || null;
        }

        return String(value);
    };

    const getAdminLocFromChanges = (changes: CclInfoTableFormValue[]): string | null => {
        const adminLocChange = changes.find((row) => row.name === "adminLoc");
        if (!adminLocChange) {
            return props.payroll?.adminLoc ?? null;
        }

        const { value } = adminLocChange;
        if (value == null || value === "") {
            return null;
        }

        if (typeof value === "object" && "val" in value) {
            return value.val || null;
        }

        return String(value);
    };

    const showSnackbarMessage = React.useCallback((message: string, severity: AlertColor) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setShowSnackbar(true);
    }, []);

    const handleInfoTableSave = (updatedData: CclInfoTableFormValue[]) => {
        const glGroup = getGlGroupFromChanges(updatedData);
        const adminLoc = getAdminLocFromChanges(updatedData);
        const projectId = props.payroll?.projectId ?? props.projectId;

        if (projectId) {
            updatePayrollProject({
                projectId,
                glGroup,
                adminLoc,
            })
                .unwrap()
                .then(() => {
                const evtData: AccessEventOFCReducedDetails= {
                    sessionID: props.sessionId,
                    projectCode: props.projectCode,
                };
                    logEvent("PayrollProjectEdited", evtData);
                    setSnackbarMessage("Payroll Summary Saved");
                    setSnackbarSeverity("success");
                    setShowSnackbar(true);
                    props.refetchPayroll();
                })
                .catch((error) => {
                    console.error("Error updating payroll project:", error);
                    setSnackbarMessage("Failed to save Payroll Summary");
                    setSnackbarSeverity("error");
                    setShowSnackbar(true);
                });
            return;
        }

        const createPayload: CreatePayrollEventRequest = {
            esKey: props.sessionId,
            eventCode: props.session.sessionCode,
            projectName: props.projectName,
            lob: null,
            adminLoc,
            client: null,
            glGroup,
        };

        createPayrollEvent(createPayload)
            .unwrap()
            .then((response) => {
                const evtData: AccessEventOFCReducedDetails= {
                    sessionID: props.sessionId,
                    projectCode: props.projectCode,
                };
                logEvent("PayrollProjectCreated", evtData);
                setSnackbarMessage("Payroll Summary Saved");
                setSnackbarSeverity("success");
                setShowSnackbar(true);
                props.refetchPayroll();
            })
            .catch((error) => {
                console.error("Error creating payroll event:", error);
                setSnackbarMessage("Failed to save Payroll Summary");
                setSnackbarSeverity("error");
                setShowSnackbar(true);
            });
    };

    const checkWorkEntryData = () => {
        if (!workEntryData || workEntryData.length === 0 || isError) {
            return false;
        }
        return true;
    }

    return (
        <Stack sx={{ width: 1, height: 1, m: 1 }} spacing={2}>
            <CclGenericAccordian
                headingText={"Summary"}
                defaultExpanded
                noGutter
                sx={{
                    width: 1,
                }}
            >
                <Stack direction={"row"} spacing={2} sx={{ width: 1, height: 1, p: 2 }}>
                    <Box sx={{ width: 1, height: 1, p: 2 }}>
                        <CclInfoTable
                            template={template}
                            data={payrollDisplayData}
                            hideSave
                        />
                    </Box>
                    <Box sx={{ width: 1, height: 1, p: 2 }}>
                        <CclInfoTable
                            template={template2}
                            data={payrollDisplayData}
                            saving={isCreatingPayrollProject || isUpdatingPayrollProject}
                            onSave={handleInfoTableSave}
                        />
                    </Box>
                </Stack>
            </CclGenericAccordian>
            {props.payroll && (         
                <CclGenericAccordian
                    headingText={"Work Entries"}
                    defaultExpanded
                    noGutter
                    sx={{
                        width: 1,
                    }}
                >
                    <WorkEntryDataGrid 
                        displayWorkEntryDetails={displayWorkEntryDetails}
                        loading={workEntryDetailsLoading}
                        revRecDate={revRecDate}
                        projectId={props.projectId}
                        projectCode={props.projectCode}
                        sessionId={props.sessionId} 
                        projectName={props.projectName} 
                        onShowSnackbar={showSnackbarMessage}
                    />
                </CclGenericAccordian>
            )}

            {checkWorkEntryData() && (
                <CclGenericAccordian
                    headingText={`Faculty Details (${workEntryId})`}
                    defaultExpanded
                    noGutter
                    sx={{
                        width: 1,
                    }}
                >
                    <TrainerEntryDataGrid projectCode={props.projectCode} sessionId={props.sessionId} workEntryData={workEntryData} />          
                </CclGenericAccordian>
            )}

            {props.payroll && (
                <CclGenericAccordian
                    headingText={"Notes"}
                    defaultExpanded
                    noGutter
                    sx={{
                        width: 1,
                    }}
                >
                    <PayrollNotes payrollKey={props.projectId} projectCode={props.projectCode} sessionId={props.sessionId} />
                </CclGenericAccordian>
            )}

            <CclAlertSnackbar
                open={showSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={() => setShowSnackbar(false)}
            />

        </Stack>
    );
};

export default SessionPayrollPanel;
